<template>
  <div class="gallery-authors">
    <div class="header">
      <h2>返图作者</h2>
    </div>

    <el-form :inline="true" class="filters">
      <el-form-item label="搜索">
        <el-input
          v-model="q"
          placeholder="昵称/ID"
          clearable
          style="width:180px"
          @keyup.enter="onFilterChange"
          @clear="onFilterChange"
        />
      </el-form-item>
      <el-form-item label="新建日期">
        <el-date-picker
          v-model="createdDate"
          type="date"
          placeholder="全部日期"
          value-format="YYYY-MM-DD"
          format="YYYY-MM-DD"
          clearable
          style="width:150px"
          @change="onFilterChange"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="load">查询</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="items" v-loading="loading" style="width:100%">
      <el-table-column label="头像" width="70">
        <template #default="{ row }">
          <el-avatar :size="36" :src="row.avatar_url || row.avatar" />
        </template>
      </el-table-column>
      <el-table-column prop="nickname" label="昵称" min-width="140" />
      <el-table-column prop="author_id" label="作者ID" width="140" show-overflow-tooltip />
      <el-table-column prop="note_count" label="收录" width="80">
        <template #header>
          <el-tooltip content="仅统计已发布笔记" placement="top">
            <span>收录</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column prop="img_count" label="图片数" width="80">
        <template #header>
          <el-tooltip content="仅统计已发布笔记的图片数" placement="top">
            <span>图片数</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column label="新建日期" width="120">
        <template #default="{ row }">
          {{ formatCreatedDate(row.created_at) }}
        </template>
      </el-table-column>
      <el-table-column label="主页" min-width="160">
        <template #default="{ row }">
          <a v-if="row.profile_url" :href="row.profile_url" target="_blank" rel="noopener">打开</a>
          <span v-else class="muted">无链接</span>
        </template>
      </el-table-column>
      <el-table-column label="隐藏" width="100">
        <template #default="{ row }">
          <el-switch
            :model-value="!!row.is_hidden"
            :loading="togglingId === row.author_id"
            inline-prompt
            active-text="隐"
            inactive-text="显"
            @change="(v) => toggleHidden(row, v)"
          />
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openEdit(row)">编辑</el-button>
          <el-button link type="primary" @click="goNotes(row)">笔记</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :total="total"
        :page-size="limit"
        :current-page="page"
        @current-change="(p) => { page = p; load() }"
      />
    </div>

    <el-drawer v-model="drawer" title="编辑作者" size="420px">
      <el-form label-width="90px" v-if="form">
        <el-form-item label="昵称">
          <el-input v-model="form.nickname" />
        </el-form-item>
        <el-form-item label="主页链接">
          <el-input v-model="form.profile_url" />
        </el-form-item>
        <el-form-item label="头像URL">
          <el-input v-model="form.avatar_url" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" type="textarea" :rows="3" />
        </el-form-item>
        <el-form-item label="软隐藏">
          <el-switch v-model="form.is_hidden" :active-value="1" :inactive-value="0" />
          <div class="hint">隐藏后前台画廊不再展示该作者笔记</div>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="saving" @click="save">保存</el-button>
        </el-form-item>
      </el-form>
    </el-drawer>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { useRouter } from 'vue-router'

export default {
  name: 'GalleryAuthors',
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const saving = ref(false)
    const togglingId = ref('')
    const items = ref([])
    const total = ref(0)
    const page = ref(1)
    const limit = ref(50)
    const q = ref('')
    const createdDate = ref('')
    const drawer = ref(false)
    const form = ref(null)

    const formatCreatedDate = (v) => {
      if (!v) return '—'
      return String(v).slice(0, 10)
    }

    const onFilterChange = () => {
      page.value = 1
      load()
    }

    const load = async () => {
      loading.value = true
      try {
        const { data } = await axios.get('/api/gallery-admin/authors', {
          params: {
            q: q.value || undefined,
            created_date: createdDate.value || undefined,
            page: page.value,
            limit: limit.value
          }
        })
        items.value = data.data?.items || []
        total.value = data.data?.total || 0
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '加载失败')
      } finally {
        loading.value = false
      }
    }

    const openEdit = (row) => {
      form.value = {
        author_id: row.author_id,
        nickname: row.nickname || '',
        profile_url: row.profile_url || '',
        avatar_url: row.avatar_url || '',
        remark: row.remark || '',
        is_hidden: row.is_hidden ? 1 : 0
      }
      drawer.value = true
    }

    const save = async () => {
      saving.value = true
      try {
        await axios.put(`/api/gallery-admin/authors/${form.value.author_id}`, form.value)
        ElMessage.success('已保存')
        drawer.value = false
        load()
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '保存失败')
      } finally {
        saving.value = false
      }
    }

    const toggleHidden = async (row, hidden) => {
      togglingId.value = row.author_id
      const prev = row.is_hidden
      row.is_hidden = hidden ? 1 : 0
      try {
        await axios.put(`/api/gallery-admin/authors/${row.author_id}`, {
          is_hidden: hidden ? 1 : 0
        })
        ElMessage.success(hidden ? '已隐藏（前台不可见）' : '已显示')
      } catch (e) {
        row.is_hidden = prev
        ElMessage.error(e.response?.data?.message || '切换失败')
      } finally {
        togglingId.value = ''
      }
    }

    const goNotes = (row) => {
      router.push({ path: '/gallery/notes', query: { author_id: row.author_id } })
    }

    onMounted(load)
    return {
      loading, saving, togglingId, items, total, page, limit, q, createdDate, drawer, form,
      formatCreatedDate, onFilterChange, load, openEdit, save, toggleHidden, goNotes
    }
  }
}
</script>

<style scoped>
.gallery-authors { padding: 8px 4px; }
.header { display:flex; justify-content:space-between; align-items:center; margin-bottom:12px; }
.header h2 { margin:0; font-size:18px; }
.filters { margin-bottom:12px; }
.filters :deep(.el-form-item__label) {
  color: #606266;
  font-weight: 500;
}
.pager { margin-top:16px; display:flex; justify-content:flex-end; }
.muted { color:#999; }
.hint { color:#999; font-size:12px; line-height:1.4; margin-top:4px; }
</style>
